import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragStartEvent,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { useAutoDismissNotice } from '../../hooks/useAutoDismissNotice';
import {
  setActiveTool,
  toggleLeftSidebar,
  openRightDrawerTab,
  setToolboxSettingsOpen,
} from '../../features/ui/uiSlice';
import {
  activateCanvasTool,
  type CanvasTool,
} from '../../features/ui/activateCanvasTool';
import { setDrawMode as setEquipmentDrawMode } from '../../features/equipment/equipmentSlice';
import { importToolbox } from '../../features/toolbox/toolboxSlice';
import { generateRoute } from '../../features/layout/generateRouteThunk';
import { generateRouteWithLlmThunk } from '../../features/layout/generateRouteLlmThunk';
import { clearGeneratedRouteThunk } from '../../features/layout/loadGeneratedRouteThunk';
import { beginEndpointCapture } from '../../features/layout/routeEndpointCapture';
import { routeEquipmentIds } from '../../features/layout/routeMarkBaseline';
import { ToolboxItem as ToolboxItemType, ToolType } from '../../types';
import { fetchToolboxCatalog } from '../../utils/toolboxApi';
import ToolboxSettings from './ToolboxSettings';

/** Category accent colors inspired by the Britelab CAD toolbox. */
const CATEGORY_COLOR: Record<string, string> = {
  Track: '#dc2626',
  OHT: '#06b6d4',
  Stocker: '#8b5cf6',
  Equipment: '#0ea5e9',
  Storage: '#f59e0b',
  Transport: '#ef4444',
  Annotations: '#6b7280',
  Actions: '#64748b',
  Other: '#94a3b8',
};

function categoryColor(category: string): string {
  return CATEGORY_COLOR[category] ?? CATEGORY_COLOR.Other!;
}

const Toolbox: React.FC = () => {
  const dispatch = useAppDispatch();
  const expanded = useAppSelector((state) => state.ui.leftSidebarOpen);
  const pointDrawActive = useAppSelector((state) => state.ui.pointDraw.active);
  const trackNodePlacementActive = useAppSelector(
    (state) => state.ui.trackNodePlacementActive
  );
  const equipmentDrawActive = useAppSelector((state) => state.equipment.draw.active);
  const equipmentCount = useAppSelector((state) => state.equipment.instanceOrder.length);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const identifyEquipmentLabel = equipmentCount
    ? `Identify Equipment (${equipmentCount})`
    : 'Identify Equipment';
  const elements = useAppSelector((state) => state.layout.elements);
  const toolboxItems = useAppSelector((state) => state.toolbox.items);
  const settingsOpen = useAppSelector((state) => state.ui.toolboxSettingsOpen);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [routeMessage, setRouteMessage] = useState<string | null>(null);
  const [llmRouteBusy, setLlmRouteBusy] = useState(false);
  const catalogLoadSeqRef = useRef(0);

  // Load toolbox icons/tools from DB whenever the editor toolbox mounts.
  useEffect(() => {
    const seq = ++catalogLoadSeqRef.current;
    void fetchToolboxCatalog()
      .then((catalog) => {
        if (catalogLoadSeqRef.current !== seq) return;
        if (catalog.tools.length > 0) {
          dispatch(importToolbox(catalog.tools));
        }
      })
      .catch((err: unknown) => {
        // Keep hardcoded seed so the editor stays usable offline.
        console.warn(
          'Toolbox catalog load failed; using local defaults.',
          err instanceof Error ? err.message : err
        );
      });
  }, [dispatch]);
  // Two-phase LLM-route button: first click starts area marking, second click generates.
  const [markingForRoute, setMarkingForRoute] = useState(false);
  // Which areas count as "marked for routing" is tracked in routeMarkBaseline (shared with the
  // assistant chat, and kept across 2D <-> 3D switches).
  const routeMessageTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRouteMessage = useCallback(() => setRouteMessage(null), []);
  useAutoDismissNotice(routeMessage, clearRouteMessage);

  /** Activate a single Actions tool, dropping whichever one was active before. */
  const selectCanvasTool = useCallback(
    (tool: CanvasTool) => {
      dispatch(activateCanvasTool(tool));
      // The LLM-route flow borrows the equipment draw tool; leaving it ends marking.
      if (tool !== 'equipment-draw') setMarkingForRoute(false);
    },
    [dispatch]
  );

  const hasOhtVehicle = useMemo(
    () => Object.values(elements).some((el) => el.type === 'oht-vehicle'),
    [elements]
  );
  const hasStocker = useMemo(
    () => Object.values(elements).some((el) => el.type === 'stocker'),
    [elements]
  );

  const handleGenerateRoute = (mode: 'tree' | 'loop') => {
    const summary = dispatch(generateRoute(mode));
    const text =
      summary.message ??
      `Generated ${summary.railCount} rail${summary.railCount === 1 ? '' : 's'}, ` +
        `${summary.curveCount} curve${summary.curveCount === 1 ? '' : 's'}, ` +
        `${summary.junctionCount} junction${summary.junctionCount === 1 ? '' : 's'} ` +
        `(${summary.totalLengthWorldUnits} units total).`;
    setRouteMessage(text);
  };

  const showRouteMessage = (text: string, clearAfterMs = 6000) => {
    if (routeMessageTimeoutRef.current) clearTimeout(routeMessageTimeoutRef.current);
    setRouteMessage(text);
    routeMessageTimeoutRef.current = setTimeout(() => setRouteMessage(null), clearAfterMs);
  };

  const handleGenerateLlmRoute = async () => {
    if (llmRouteBusy) return;

    // Phase 1: start marking the equipment areas (reuses the Identify-Equipment draw tool).
    if (!markingForRoute) {
      // Starting a new route: drop the previous generated route + its old START/END markers so
      // they don't linger next to the points about to be placed.
      dispatch(clearGeneratedRouteThunk());
      selectCanvasTool('equipment-draw');
      setMarkingForRoute(true);
      dispatch(setEquipmentDrawMode('rectangle'));
      dispatch(openRightDrawerTab('equipment'));
      showRouteMessage('Mark the equipment areas, then click the button again to generate.', 8000);
      return;
    }

    // Phase 2: finish marking and generate from EVERY area the user identified by hand — drawn
    // just now, before the route flow started, or after an earlier generation. DXF-extracted
    // areas are never routed.
    selectCanvasTool(null);
    const newIds = routeEquipmentIds(equipmentInstances);
    if (newIds.length === 0) {
      showRouteMessage('No equipment you marked was found — draw at least one area, then click again.');
      return;
    }
    // Capture the route START then END points on the canvas (Esc skips them).
    showRouteMessage('Click the START point on the canvas, then the END point. (Esc to skip.)', 60000);
    let endpoints:
      | { start: { x: number; y: number }; end: { x: number; y: number } }
      | undefined;
    try {
      endpoints = await beginEndpointCapture(dispatch);
    } catch {
      endpoints = undefined;
    }
    setLlmRouteBusy(true);
    showRouteMessage(`Generating route from ${newIds.length} marked area(s)…`, 60000);
    try {
      const result = await dispatch(generateRouteWithLlmThunk(newIds, endpoints));
      showRouteMessage(result.message);
    } catch (err) {
      showRouteMessage(`LLM route failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLlmRouteBusy(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const toolType = event.active.id as ToolType;
    // Starting a drag & drop placement cancels the Actions tools so only one
    // placement workflow is ever active at a time.
    selectCanvasTool(null);
    dispatch(setActiveTool(toolType));
  };

  const handleDragEnd = () => {
    dispatch(setActiveTool('select'));
  };

  const groupedItems = useMemo(() => {
    return toolboxItems
      .filter((item) => item.visibleInToolbox !== false)
      .reduce(
        (acc, item) => {
          const category = item.category || 'Other';
          if (!acc[category]) acc[category] = [];
          acc[category].push(item);
          return acc;
        },
        {} as Record<string, ToolboxItemType[]>
      );
  }, [toolboxItems]);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => dispatch(toggleLeftSidebar())}
        title="Open tools"
        aria-label="Open tools"
        className="absolute top-3 left-3 z-20 inline-flex items-center gap-1.5 rounded-xl border border-slate-200/80 bg-white/95 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.9px] text-slate-500 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-700"
      >
        Tools
        <ChevronRightIcon />
      </button>
    );
  }

  return (
    <>
      <aside
        className="absolute top-3 left-3 bottom-3 z-30 flex w-[120px] min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_8px_24px_-6px_rgba(15,23,42,0.18)]"
        aria-label="Toolbox"
      >
        {/* Header — Figma: TOOLS + collapse */}
        <div className="shrink-0 border-b border-slate-900/10 px-2.5 py-2.5">
          <div className="flex items-center justify-between gap-1">
            <p className="truncate text-[10px] font-semibold uppercase tracking-[0.8px] text-slate-600">
              Tools
            </p>
            <div className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                onClick={() => dispatch(setToolboxSettingsOpen(!settingsOpen))}
                title="Toolbox settings"
                aria-label="Toolbox settings"
                className={`flex rounded-md p-1 transition-colors ${
                  settingsOpen
                    ? 'bg-slate-100 text-slate-700'
                    : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                }`}
              >
                <SettingsIcon />
              </button>
              <button
                type="button"
                onClick={() => dispatch(toggleLeftSidebar())}
                title="Collapse tools"
                aria-label="Collapse tools"
                className="flex rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              >
                <ChevronLeftIcon />
              </button>
            </div>
          </div>
        </div>

        <ToolboxSettings isOpen={settingsOpen} onClose={() => dispatch(setToolboxSettingsOpen(false))} />

        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          {/* Compact actions row */}
          <CategoryHeader
            label="Actions"
            color={categoryColor('Actions')}
            collapsed={collapsedCategories.has('Actions')}
            onToggle={() => toggleCategory('Actions')}
          />
          {!collapsedCategories.has('Actions') && (
            <div className="grid grid-cols-2 gap-2 px-2.5 pb-2">
              <IconAction
                active={pointDrawActive}
                title="Point Draw (click points to place track; Enter finishes, Esc cancels)"
                onClick={() => selectCanvasTool(pointDrawActive ? null : 'point-draw')}
              >
                <PointDrawIcon />
              </IconAction>
              <IconAction
                active={equipmentDrawActive}
                title={
                  equipmentDrawActive ? 'Stop identifying equipment' : identifyEquipmentLabel
                }
                onClick={() => {
                  const next = !equipmentDrawActive;
                  selectCanvasTool(next ? 'equipment-draw' : null);
                  if (next) dispatch(openRightDrawerTab('equipment'));
                }}
              >
                <TagIcon />
              </IconAction>
              <IconAction
                disabled={!hasOhtVehicle || !hasStocker}
                title="Generate Route"
                onClick={() => handleGenerateRoute('tree')}
              >
                <RouteIcon />
              </IconAction>
              <IconAction
                disabled={!hasOhtVehicle || !hasStocker}
                title="Generate Loop"
                onClick={() => handleGenerateRoute('loop')}
              >
                <LoopIcon />
              </IconAction>
              <IconAction
                active={llmRouteBusy || markingForRoute}
                disabled={llmRouteBusy}
                title={
                  llmRouteBusy
                    ? 'Generating route with LLM…'
                    : markingForRoute
                      ? 'Finish marking & generate route'
                      : 'Generate Route with LLM (click, mark the equipment areas, then click again)'
                }
                onClick={handleGenerateLlmRoute}
              >
                {llmRouteBusy ? (
                  <SpinnerIcon />
                ) : markingForRoute ? (
                  <FinishMarkIcon />
                ) : (
                  <LlmRouteIcon />
                )}
              </IconAction>
              <IconAction
                active={trackNodePlacementActive}
                title="Add Track Node (click a straight track; Esc exits)"
                onClick={() =>
                  selectCanvasTool(trackNodePlacementActive ? null : 'track-node')
                }
              >
                <TrackNodeIcon />
              </IconAction>
            </div>
          )}

          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            {Object.entries(groupedItems).map(([category, items]) => {
              const color = categoryColor(category);
              const isCollapsed = collapsedCategories.has(category);
              return (
                <div key={category}>
                  <CategoryHeader
                    label={category}
                    color={color}
                    collapsed={isCollapsed}
                    onToggle={() => toggleCategory(category)}
                  />
                  {!isCollapsed && (
                    <div className="grid grid-cols-2 gap-2 px-2.5 pb-2">
                      {items.map((item) => (
                        <ToolboxItem
                          key={item.id}
                          item={item}
                          accent={color}
                          onCatalogInteract={() => selectCanvasTool(null)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </DndContext>
        </div>

        <div className="shrink-0 border-t border-slate-900/10 px-2.5 py-2.5">
          <p className="text-center text-[10px] leading-snug text-slate-500">
            Drag tools onto the canvas.
          </p>
        </div>
      </aside>

      {routeMessage && (
        <div className="fixed top-16 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2 text-sm text-white shadow-lg">
          {routeMessage}
        </div>
      )}
    </>
  );
};

const CategoryHeader: React.FC<{
  label: string;
  color: string;
  collapsed: boolean;
  onToggle: () => void;
}> = ({ label, color, collapsed, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="flex w-full min-w-0 items-center justify-between gap-1 px-2.5 py-1.5 text-left hover:bg-slate-50/80"
  >
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span
        className="min-w-0 truncate text-[10px] font-semibold uppercase tracking-[0.4px]"
        style={{ color }}
        title={label}
      >
        {label}
      </span>
    </span>
    <span
      className={`shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
      style={{ color }}
    >
      <CategoryChevronIcon />
    </span>
  </button>
);

const IconAction: React.FC<{
  active?: boolean;
  disabled?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, disabled, title, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    aria-pressed={active}
    className={`flex size-10 items-center justify-center rounded-lg border transition-colors ${
      disabled
        ? 'cursor-not-allowed border-transparent opacity-35'
        : active
          ? 'border-sky-300 bg-sky-50 text-sky-700 shadow-sm'
          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900'
    }`}
  >
    {children}
  </button>
);

interface ToolboxItemProps {
  item: ToolboxItemType;
  accent: string;
  /** Clears any Actions tool before placing / dragging this catalog item. */
  onCatalogInteract: () => void;
}

const StraightTrackIcon: React.FC<{ vertical?: boolean; color: string }> = ({
  vertical,
  color,
}) => (
  <div className="flex size-7 items-center justify-center" aria-hidden>
    <div
      className="rounded-sm"
      style={{
        backgroundColor: color,
        width: vertical ? 4 : 22,
        height: vertical ? 22 : 4,
      }}
    />
  </div>
);

/** Shallow 45° arc — distinct from the straight diagonal slash glyph. */
const Curve45TrackIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 18 C4 18 6 18 10 16 C14 14 16 10 18 6"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ToolboxItem: React.FC<ToolboxItemProps> = ({ item, accent, onCatalogInteract }) => {
  const [isDragging, setIsDragging] = React.useState(false);

  const handleClick = () => {
    // Placing a catalog item ends any Actions tool (Point Draw, equipment, …).
    onCatalogInteract();
    window.dispatchEvent(new CustomEvent('toolbox:add-item', { detail: item }));
  };

  const isImageUrl = item.icon.startsWith('data:image') || item.icon.startsWith('http');
  const isStraightTrack =
    item.type === 'overhead-rail' && item.defaultProperties?.railType === 'straight';
  const isCurve45 =
    item.type === 'overhead-curve' && item.defaultProperties?.railType === 'curve45';

  return (
    <div
      draggable
      onClick={handleClick}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('application/json', JSON.stringify(item));
        // HTML5 drag is the real catalog path — clear Point Draw / equipment /
        // track-node so only one placement workflow stays active.
        onCatalogInteract();
        window.dispatchEvent(new CustomEvent('toolbox:drag-item-start', { detail: item }));
        setIsDragging(true);
      }}
      onDragEnd={() => {
        window.dispatchEvent(new CustomEvent('toolbox:drag-item-end'));
        setIsDragging(false);
      }}
      title={item.name}
      aria-label={item.name}
      className={`flex size-10 cursor-grab items-center justify-center rounded-lg border border-slate-200 bg-white transition-colors hover:border-slate-300 hover:bg-slate-50 active:cursor-grabbing ${
        isDragging ? 'opacity-50' : 'opacity-100'
      }`}
      style={{ color: accent }}
    >
      <div className="flex size-8 items-center justify-center overflow-hidden">
        {isStraightTrack ? (
          <StraightTrackIcon vertical={item.defaultRotation === 90} color={accent} />
        ) : isCurve45 ? (
          <Curve45TrackIcon color={accent} />
        ) : isImageUrl ? (
          <img src={item.icon} alt="" className="size-full object-contain" />
        ) : (
          <span className="text-lg font-bold leading-none">{item.icon}</span>
        )}
      </div>
    </div>
  );
};

/** Icon for the Point Draw tool: two connected vertices with a segment. */
const PointDrawIcon: React.FC = () => (
  <svg
    className="w-4 h-4 text-slate-700 dark:text-slate-200"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <line x1="4" y1="12" x2="12" y2="4" strokeLinecap="round" />
    <circle cx="3.5" cy="12.5" r="2" fill="currentColor" stroke="none" />
    <circle cx="12.5" cy="3.5" r="2" fill="currentColor" stroke="none" />
  </svg>
);

/** Track with a highlighted insertable network node. */
const TrackNodeIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden
  >
    <path d="M3 12h18" strokeLinecap="round" />
    <circle cx="12" cy="12" r="4" fill="#facc15" stroke="#ca8a04" />
  </svg>
);

const ChevronRightIcon: React.FC = () => (
  <svg className="size-3.5" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75">
    <path d="M5 3.5 9 7 5 10.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronLeftIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CategoryChevronIcon: React.FC = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SettingsIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path
      d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V19a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H5a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V5a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H19a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TagIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
    <path
      d="M20.6 13.4 12 22l-8.6-8.6a2 2 0 0 1 0-2.8L11 3h7a2 2 0 0 1 2 2v7z"
      strokeLinejoin="round"
    />
    <circle cx="16" cy="8" r="1.3" fill="currentColor" stroke="none" />
  </svg>
);

const RouteIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
    <circle cx="6" cy="19" r="2" />
    <circle cx="18" cy="5" r="2" />
    <path d="M8 19h6a4 4 0 0 0 4-4V7" strokeLinecap="round" />
  </svg>
);

const LoopIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
    <path d="M17 2v4h4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 22v-4H3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 18" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** AI / sparkle wand — the "generate route with LLM" action. */
const LlmRouteIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path
      d="M5 3v4M3 5h4M6 17v4M4 19h4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 4l1.8 4.2L20 10l-4.2 1.8L14 16l-1.8-4.2L8 10l4.2-1.8L14 4z"
      strokeLinejoin="round"
    />
  </svg>
);

const SpinnerIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    className="animate-spin"
  >
    <path d="M12 3a9 9 0 1 0 9 9" strokeLinecap="round" />
  </svg>
);

/** Shown while marking areas — click to finish marking and generate. */
const FinishMarkIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25">
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12.5l2.5 2.5 4.5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default Toolbox;
